import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { TrendDataPoint } from '../../types';
import { colors } from '../../styles/theme';
import {
  committedSections,
  PROPOSED_SECTION,
  SECTION_LABELS,
  type CommittedSection,
} from '../../utils/analyticsSections';
import { getStageOrder, stageIndex } from '../../utils/stageOrder';
import { DropdownToggle } from '../shared/DropdownToggle';
import styles from './PipelineTrendChart.module.css';

// Legacy hex for the committed sections (they predate the tokens-only rule); the candidate band
// reads the theme. Labels are now shared with Pipeline by Sector, so the two charts can no longer
// drift apart on what to call the same series (A8 — this chart used to say just "Pending").
const SECTION_COLORS: Record<string, string> = {
  live: '#1A1A1A',
  screening: '#E65100',
  pending: '#999',
  origination: '#1565C0',
  [PROPOSED_SECTION]: colors.candidate.proposed,
};

// Stack order: most-advanced on TOP, least-advanced on BOTTOM. Recharts
// stacks in declaration order with the first <Bar> at the bottom of the
// stack, so we list least-advanced first.
const SECTION_STACK_BOTTOM_TO_TOP: CommittedSection[] = ['origination', 'pending', 'screening', 'live'];

// Color ramp for the stage view — least-advanced → most-advanced. Picked to
// keep Live-end stages in the existing dark tone and Mapping in the
// existing origination blue, so users get visual continuity between modes.
const STAGE_COLOR_RAMP = ['#1565C0', '#1976D2', '#7B1FA2', '#E65100', '#F57C00', '#5C5C5C', '#1A1A1A'];

function stageColor(stage: string, totalStages: number): string {
  const idx = stageIndex(stage);
  if (idx >= totalStages) return '#999';
  // stageIndex: 0 = most-advanced. Map most-advanced → end of ramp (darker).
  const rampIdx = STAGE_COLOR_RAMP.length - 1 - Math.round((idx / Math.max(1, totalStages - 1)) * (STAGE_COLOR_RAMP.length - 1));
  return STAGE_COLOR_RAMP[Math.max(0, Math.min(STAGE_COLOR_RAMP.length - 1, rampIdx))];
}

type Mode = 'section' | 'stage';

interface Props {
  data: TrendDataPoint[];
  snapshotCount: number;
  /** The page's "Include AI-proposed deals" lens. ON adds the candidate band at the bottom of the
   *  status-group stack; the status (stage) view never shows candidates — a candidate's stage is a
   *  PROJECTED value and mixing it into stage buckets would corrupt the committed counts. */
  includeCandidates?: boolean;
}

export function PipelineTrendChart({ data, snapshotCount, includeCandidates = false }: Props) {
  const [mode, setMode] = useState<Mode>('section');

  // A1: a status group is drawn only when some week actually recorded it (live + origination
  // always). The candidate band sits at the BOTTOM of the stack — it is the least-advanced thing on
  // the chart, and keeping it under the committed bands leaves those reading as they do today.
  const sectionStack = useMemo(() => {
    const drawn = committedSections(data);
    const stack: string[] = SECTION_STACK_BOTTOM_TO_TOP.filter((s) => drawn.includes(s));
    return includeCandidates ? [PROPOSED_SECTION, ...stack] : stack;
  }, [data, includeCandidates]);

  // All stages observed in the trend window, sorted least-advanced first
  // (bottom of the stack). stageIndex returns 0 for most-advanced, so we
  // sort by descending index.
  const observedStages = useMemo(() => {
    const set = new Set<string>();
    for (const point of data) {
      if (!point.by_stage) continue;
      for (const stage of Object.keys(point.by_stage)) set.add(stage);
    }
    return Array.from(set).sort((a, b) => stageIndex(b) - stageIndex(a));
  }, [data]);

  // Flatten by_stage into top-level keys for Recharts. Recharts only reads
  // top-level fields on the data points; nested dicts won't render. Keeping
  // the original TrendDataPoint fields alongside the stage keys lets us
  // share one chartData type across both modes.
  const chartData = useMemo<TrendDataPoint[]>(() => {
    // `proposed` is defaulted here, not in the Bar: a week recorded before the producer shipped the
    // bucket carries no field at all, and recharts draws `undefined` as a HOLE in the stack rather
    // than a zero-height segment.
    if (mode === 'section') return data.map((point) => ({ ...point, proposed: point.proposed ?? 0 }));
    return data.map((point) => {
      const stageKeys: Record<string, number> = {};
      for (const stage of observedStages) {
        stageKeys[stage] = point.by_stage?.[stage] ?? 0;
      }
      return { ...point, ...stageKeys };
    });
  }, [data, mode, observedStages]);

  const stageOrderLen = getStageOrder().length;
  const stageGranularityAvailable = observedStages.length > 0;

  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <span className={styles.headerLabel}>Pipeline Composition</span>
        <span className={styles.headerSub}>
          {snapshotCount} week{snapshotCount !== 1 ? 's' : ''} recorded
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <DropdownToggle<Mode>
            variant="dark"
            ariaLabel="Composition granularity"
            value={mode}
            onChange={(v) => {
              if (v === 'stage' && !stageGranularityAvailable) return;
              setMode(v);
            }}
            options={[
              { value: 'section', label: 'By status group' },
              { value: 'stage', label: 'By status' },
            ]}
          />
        </div>
      </div>
      {snapshotCount < 2 ? (
        <div className={styles.placeholder}>
          Trend data will appear once two weeks have been recorded.
          <br />
          {/* Decision 35 as amended 2026-09-03: the pre-cut composition was converted into the
              same weekly series, so this no longer says the history is archived and undrawn. */}
          The series is every week that was recorded &mdash; the converted pre-cut history, and one
          point a week since. A week nothing measured stays a gap.
        </div>
      ) : (
        <div className={styles.body}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="period_label"
                tick={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fill: '#999' }}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fill: '#999' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #E5E5E0',
                }}
              />
              <Legend
                wrapperStyle={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  textTransform: 'uppercase',
                }}
              />
              {mode === 'section'
                ? sectionStack.map((section, i) => (
                    <Bar
                      key={section}
                      dataKey={section}
                      name={SECTION_LABELS[section]}
                      stackId="a"
                      fill={SECTION_COLORS[section]}
                      radius={i === sectionStack.length - 1 ? [3, 3, 0, 0] : undefined}
                    />
                  ))
                : observedStages.map((stage, i) => (
                    <Bar
                      key={stage}
                      dataKey={stage}
                      name={stage}
                      stackId="a"
                      fill={stageColor(stage, stageOrderLen)}
                      radius={i === observedStages.length - 1 ? [3, 3, 0, 0] : undefined}
                    />
                  ))}
            </BarChart>
          </ResponsiveContainer>
          {/* ovw-3 (owner rulings 2026-09-21): this band's PAST weeks are derived and recomputed
              daily, so what the chart says about a week changes when a proposal is decided. The
              caption is where the page says so — one line, and only where the band is actually
              drawn: the stage view has no candidates in it, so there it would describe something
              that is not on screen. */}
          {includeCandidates && mode === 'section' && (
            <p className={styles.caption} data-testid="composition-caption">
              AI Proposed = proposals awaiting a decision, as known today. Accepted deals count in
              their section from the proposal date; merged and dismissed proposals are excluded
              from every week.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
